/**
 * Pilha de camadas GOES (até 3) — CONTRATO COMPARTILHADO.
 *
 * Dono: app-core. Outros módulos (ex.: exportação) só LEEM `layers` para
 * montar títulos/legendas. O dono pode acrescentar campos e ações, mas não deve
 * renomear os existentes.
 */
import { create } from 'zustand';
import { DEFAULT_PRODUCT_ID, MAX_GOES_LAYERS } from '../config/products';

export interface GoesLayerState {
  /** Identificador estável da camada na pilha (não é o produto). */
  uid: string;
  /** Produto do STAR (ver src/config/products.ts). */
  productId: string;
  /** 0–1. */
  opacity: number;
  visible: boolean;
}

export interface GoesState {
  /** Da base (índice 0, desenhada por baixo) para o topo. */
  layers: GoesLayerState[];
  /**
   * Camadas (uid) que já estavam escondidas quando a pilha inteira foi
   * desligada pelo trilho, para religar a mesma seleção. null = pilha ligada.
   */
  hiddenBeforeOff: string[] | null;
  addLayer: (productId: string) => void;
  removeLayer: (uid: string) => void;
  updateLayer: (uid: string, patch: Partial<Omit<GoesLayerState, 'uid'>>) => void;
  moveLayer: (uid: string, direction: -1 | 1) => void;
  setLayers: (layers: GoesLayerState[]) => void;
  /**
   * Interruptor do trilho: desliga a pilha inteira ou religa a seleção
   * anterior (todas, se nenhuma estava visível — religar tem de mostrar algo).
   */
  setStackVisible: (on: boolean) => void;
}

let seq = 0;
export const newLayerUid = () => `g${Date.now().toString(36)}${(seq++).toString(36)}`;

export const useGoesStore = create<GoesState>()((set) => ({
  layers: [{ uid: newLayerUid(), productId: DEFAULT_PRODUCT_ID, opacity: 1, visible: true }],
  hiddenBeforeOff: null,

  addLayer: (productId) =>
    set((s) =>
      s.layers.length >= MAX_GOES_LAYERS
        ? s
        : { layers: [...s.layers, { uid: newLayerUid(), productId, opacity: 0.6, visible: true }] },
    ),

  removeLayer: (uid) =>
    set((s) => (s.layers.length <= 1 ? s : { layers: s.layers.filter((l) => l.uid !== uid) })),

  updateLayer: (uid, patch) =>
    set((s) => ({ layers: s.layers.map((l) => (l.uid === uid ? { ...l, ...patch } : l)) })),

  moveLayer: (uid, direction) =>
    set((s) => {
      const i = s.layers.findIndex((l) => l.uid === uid);
      const j = i + direction;
      if (i < 0 || j < 0 || j >= s.layers.length) return s;
      const layers = [...s.layers];
      [layers[i], layers[j]] = [layers[j], layers[i]];
      return { layers };
    }),

  setLayers: (layers) => set({ layers: layers.slice(0, MAX_GOES_LAYERS) }),

  setStackVisible: (on) =>
    set((s) => {
      const anyOn = s.layers.some((l) => l.visible);
      if (!on) {
        if (!anyOn) return s;
        return {
          layers: s.layers.map((l) => ({ ...l, visible: false })),
          hiddenBeforeOff: s.layers.filter((l) => !l.visible).map((l) => l.uid),
        };
      }
      if (anyOn) return s;
      const hidden = new Set(s.hiddenBeforeOff ?? []);
      let layers = s.layers.map((l) => ({ ...l, visible: !hidden.has(l.uid) }));
      if (!layers.some((l) => l.visible)) layers = layers.map((l) => ({ ...l, visible: true }));
      return { layers, hiddenBeforeOff: null };
    }),
}));
